import React, { useCallback, useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import { Alert } from '@mui/material';
import { AlertTriangle, ExternalLink } from 'react-feather';
import { DialogContainer, MessageDialog } from '~/src/components/Modal/mui';
import env from '~/src/utils/env';
import analyticsService from '~/src/services/analytics';

export type RegenerateModalProps = {
  onConfirm: () => void;
  onCancel: () => void;
  documentSetName?: string;
  shouldOpen: boolean;
  newVersionAvailable?: boolean;
  fromClioDraft?: boolean;
  clioMatterId?: string;
  clioParentId?: string;
};

const REGENERATE_MODAL_DISMISSED_KEY = 'regenerateModalDismissed';

const RegenerateModal = ({
  onConfirm,
  onCancel,
  documentSetName = 'Folder',
  shouldOpen,
  newVersionAvailable = false,
  fromClioDraft = false,
  clioMatterId,
  clioParentId,
}: RegenerateModalProps) => {
  // Used to track if the modal has been dismissed based on local storage value
  const [isModalDismissed, setIsModalDismissed] = useState(false);
  // Used to track if the user has clicked the "Do not show this message again" checkbox
  const [dontShowAgain, setDontShowAgain] = useState(false);

  useEffect(() => {
    setIsModalDismissed(getModalDismissedStatus());
  }, []);

  // Track when modal is shown
  useEffect(() => {
    if (shouldOpen && !getModalDismissedStatus()) {
      const modalVariant = fromClioDraft ? 'clio_draft' : 'clio_manage';

      analyticsService.track('Regenerate Modal Shown', {
        source: modalVariant,
        from_clio_draft: fromClioDraft,
        new_version_available: newVersionAvailable,
      });
    }
  }, [shouldOpen, fromClioDraft, newVersionAvailable]);

  const handleConfirm = useCallback(() => {
    const modalVariant = fromClioDraft ? 'clio_draft' : 'clio_manage';

    analyticsService.track('Regenerate Modal Continue Clicked', {
      source: modalVariant,
      from_clio_draft: fromClioDraft,
      new_version_available: newVersionAvailable,
    });

    try {
      localStorage.setItem(
        REGENERATE_MODAL_DISMISSED_KEY,
        dontShowAgain.toString(),
      );
    } catch {
      return;
    }
    onConfirm();
  }, [onConfirm, dontShowAgain, fromClioDraft, newVersionAvailable]);

  const getModalDismissedStatus = () => {
    try {
      return localStorage.getItem(REGENERATE_MODAL_DISMISSED_KEY) === 'true';
    } catch {
      return false;
    }
  };

  const handleGoToClioManage = useCallback(() => {
    if (!clioMatterId || !clioParentId) {
      console.error(
        'Missing clioMatterId or clioParentId for Clio Manage navigation',
      );
      return;
    }

    const modalVariant = fromClioDraft ? 'clio_draft' : 'clio_manage';

    analyticsService.track('Regenerate Modal Go to Clio Manage Clicked', {
      source: modalVariant,
      from_clio_draft: fromClioDraft,
      new_version_available: newVersionAvailable,
      clio_matter_id: clioMatterId,
      clio_parent_id: clioParentId,
    });

    const clioManageUrl = `${env.clioBaseUrl}/nc/#/matters/${clioMatterId}/documents?folderId=${clioParentId}`;

    window.open(clioManageUrl, '_blank', 'noopener,noreferrer');
  }, [clioMatterId, clioParentId, fromClioDraft, newVersionAvailable]);

  const handleCancel = useCallback(() => {
    const modalVariant = fromClioDraft ? 'clio_draft' : 'clio_manage';

    analyticsService.track('Regenerate Modal Cancelled', {
      source: modalVariant,
      from_clio_draft: fromClioDraft,
      new_version_available: newVersionAvailable,
    });

    onCancel();
  }, [onCancel, fromClioDraft, newVersionAvailable]);

  // Don't render anything if modal should not be shown
  if (!shouldOpen || (isModalDismissed && !newVersionAvailable)) {
    return null;
  }

  const isAppWithNewVersion = fromClioDraft && newVersionAvailable;

  const getModalTitle = () => {
    if (isAppWithNewVersion) {
      return 'Changes made in Clio Manage are not reflected';
    }
    return 'Regenerate with Clio Draft';
  };

  const getModalBodyContent = () => {
    if (isAppWithNewVersion) {
      return (
        <div>
          <div style={{ marginBottom: 16 }}>
            Changes have been made to the contents of the documents saved in
            Clio Manage and these are not reflected in Clio Draft.
          </div>

          <div style={{ marginBottom: 24 }}>
            If you make changes to this document / document set in Clio Draft
            and save it to Clio Manage, a new version will be created.
          </div>
        </div>
      );
    }

    // Default content for regular regenerate modal
    return (
      <div>
        <div style={{ marginBottom: 16 }}>
          You will be making changes to the source document that
          <strong style={{ marginLeft: '4px' }}>{documentSetName}</strong> was
          saved from.
        </div>

        <div style={{ marginBottom: 24 }}>
          If you save this document back into Clio Manage, a new version will be
          created.
        </div>
      </div>
    );
  };

  // Get dialog actions based on variant
  const getDialogActions = () => {
    if (isAppWithNewVersion) {
      return (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            variant="outlined"
            onClick={handleGoToClioManage}
            endIcon={<ExternalLink size={16} />}
            sx={{
              borderColor: 'primary.main',
              color: 'primary.main',
              '&:hover': {
                borderColor: 'primary.dark',
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
              },
            }}
          >
            Go to Clio Manage
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            sx={{ backgroundColor: 'primary.main' }}
          >
            Continue
          </Button>
        </div>
      );
    }

    return (
      <Button
        variant="contained"
        onClick={handleConfirm}
        sx={{ backgroundColor: 'primary.main' }}
      >
        Continue
      </Button>
    );
  };

  return (
    <DialogContainer open={true} onClose={handleCancel}>
      <MessageDialog
        title={getModalTitle()}
        onClose={handleCancel}
        dialogActions={getDialogActions()}
      >
        <div>
          {getModalBodyContent()}

          {newVersionAvailable && (
            <div style={{ marginBottom: 16 }}>
              <Alert
                sx={{
                  padding: '8px 16px',
                  background: '#fffbea',
                  border: '1px solid #d0d0d0',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  color: '#000000',
                  '.MuiAlert-icon': {
                    color: '#000000',
                  },
                }}
                icon={<AlertTriangle fontSize="inherit" />}
                severity="warning"
              >
                If you don&apos;t want to lose changes made in Clio Manage, edit
                the document in Clio Manage with Clio Desktop or PDF Editor.
              </Alert>
            </div>
          )}

          {!newVersionAvailable && (
            <div>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  style={{ marginRight: '8px' }}
                  checked={dontShowAgain}
                  onChange={(e) => setDontShowAgain(e.target.checked)}
                />
                Do not show this message again
              </label>
            </div>
          )}
        </div>
      </MessageDialog>
    </DialogContainer>
  );
};

export default RegenerateModal;
